import { useCallback, useEffect, useState } from "react";
import { Box, IconButton, Slide, Stack, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import { useNavigate } from "react-router-dom";
import {
  FCM_FOREGROUND_EVENT,
  type FcmForegroundMessage,
} from "../../utils/fcmForegroundEvents";
import {
  navigateToNotificationTarget,
  resolveNotificationNavigation,
} from "../../utils/notificationsApi";

const AUTO_DISMISS_MS = 7000;
const MAX_VISIBLE = 4;

const ForegroundNotificationToast = (): JSX.Element => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<FcmForegroundMessage[]>([]);

  const dismiss = useCallback((id: string) => {
    setMessages((prev) => prev.filter((m) => m.id !== id));
  }, []);

  const handleToastClick = useCallback(
    (msg: FcmForegroundMessage) => {
      const type = msg.data?.type ?? msg.data?.notificationType ?? "";
      const target = resolveNotificationNavigation(type, msg.data);
      if (target) {
        navigateToNotificationTarget(navigate, target);
      }
      dismiss(msg.id);
    },
    [navigate, dismiss]
  );

  useEffect(() => {
    const onFcmMessage = (event: Event) => {
      const detail = (event as CustomEvent<FcmForegroundMessage>).detail;
      if (!detail?.id) return;

      setMessages((prev) => [detail, ...prev].slice(0, MAX_VISIBLE));

      window.setTimeout(() => {
        setMessages((prev) => prev.filter((m) => m.id !== detail.id));
      }, AUTO_DISMISS_MS);
    };

    window.addEventListener(FCM_FOREGROUND_EVENT, onFcmMessage);
    return () => window.removeEventListener(FCM_FOREGROUND_EVENT, onFcmMessage);
  }, []);

  if (!messages.length) return <></>;

  return (
    <Box
      sx={{
        position: "fixed",
        top: { xs: 12, sm: 20 },
        right: { xs: 12, sm: 20 },
        zIndex: (theme) => theme.zIndex.snackbar + 2,
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        width: { xs: "calc(100vw - 24px)", sm: 380 },
        maxWidth: 380,
        pointerEvents: "none",
      }}
    >
      {messages.map((msg) => (
        <Slide key={msg.id} direction="left" in mountOnEnter unmountOnExit>
          <Box
            role="alert"
            onClick={() => handleToastClick(msg)}
            sx={{
              pointerEvents: "auto",
              display: "flex",
              gap: 1.5,
              p: 1.75,
              borderRadius: "10px",
              background: "#ffffff",
              border: "1px solid #E6E4FE",
              boxShadow: "0 4px 16px rgba(117, 109, 233, 0.15), 0 2px 8px rgba(0, 0, 0, 0.06)",
              overflow: "hidden",
              position: "relative",
              fontFamily: "'Poppins', sans-serif",
              cursor: "pointer",
              "&::before": {
                content: '""',
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: 3,
                background: "#756DE9",
              },
            }}
          >
            <Box
              sx={{
                ml: 0.5,
                width: 44,
                height: 44,
                borderRadius: "10px",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(117, 109, 233, 0.12)",
                color: "#756DE9",
              }}
            >
              {msg.imageUrl ? (
                <Box
                  component="img"
                  src={msg.imageUrl}
                  alt=""
                  sx={{ width: 44, height: 44, borderRadius: "12px", objectFit: "cover" }}
                />
              ) : (
                <NotificationsActiveOutlinedIcon sx={{ fontSize: 24 }} />
              )}
            </Box>

            <Stack spacing={0.35} sx={{ flex: 1, minWidth: 0, pr: 3 }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  textTransform: "uppercase",
                  color: "#756DE9",
                }}
              >
                New notification
              </Typography>
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#1F2235",
                  lineHeight: 1.3,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {msg.title}
              </Typography>
              {msg.body ? (
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 400,
                    color: "#757575",
                    lineHeight: 1.45,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {msg.body}
                </Typography>
              ) : null}
            </Stack>

            <IconButton
              size="small"
              aria-label="Dismiss notification"
              onClick={(e) => {
                e.stopPropagation();
                dismiss(msg.id);
              }}
              sx={{
                position: "absolute",
                top: 6,
                right: 6,
                color: "#888",
                "&:hover": { color: "#756DE9", backgroundColor: "rgba(117, 109, 233, 0.08)" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Slide>
      ))}
    </Box>
  );
};

export default ForegroundNotificationToast;
